import { ConfigurationMapEdge } from '@rossum/api-client/internal';

// Edge keys enable precise highlighting when hooks/rules are added (next MR) —
// node-based check is insufficient when BFS skips intermediate nodes.
type BfsResult = { nodeIds: Set<string>; edges: Set<string> };

/**
 * Expand the BFS frontier by one step: find all nodes reachable from the
 * current frontier that haven't been visited yet. Returns the new nodes
 * and their connecting edge keys.
 */
const expandFrontier = (
  frontier: Set<string>,
  visited: Set<string>,
  edges: ConfigurationMapEdge[],
  direction: (e: ConfigurationMapEdge) => [from: string, to: string],
  accept?: (to: string) => boolean
): { nextNodes: Set<string>; nextEdges: Set<string> } => {
  const nextNodes = new Set<string>();
  const nextEdges = new Set<string>();
  edges.forEach(edge => {
    const [from, to] = direction(edge);
    if (!frontier.has(from) || visited.has(to)) return;
    if (accept && !accept(to)) return;
    nextNodes.add(to);
    nextEdges.add(`${edge.source}-${edge.target}`);
  });
  return { nextNodes, nextEdges };
};

/**
 * BFS walk from `start` along `edges` in the given `direction`.
 * Returns all reachable node IDs and the edge keys traversed.
 */
const bfsWalk = (
  start: Set<string>,
  edges: ConfigurationMapEdge[],
  direction: (e: ConfigurationMapEdge) => [from: string, to: string],
  accept?: (to: string) => boolean
): BfsResult => {
  const step = (
    frontier: Set<string>,
    visited: Set<string>,
    collectedEdges: Set<string>
  ): BfsResult => {
    if (frontier.size === 0) return { nodeIds: visited, edges: collectedEdges };

    const { nextNodes, nextEdges } = expandFrontier(
      frontier,
      visited,
      edges,
      direction,
      accept
    );

    return step(
      nextNodes,
      new Set([...visited, ...nextNodes]),
      new Set([...collectedEdges, ...nextEdges])
    );
  };

  return step(start, new Set(start), new Set());
};

const upstream = (e: ConfigurationMapEdge): [string, string] => [
  e.target,
  e.source,
];
const downstream = (e: ConfigurationMapEdge): [string, string] => [
  e.source,
  e.target,
];

/** Merge multiple BFS results into one. */
const mergeResults = (...results: BfsResult[]): BfsResult => ({
  nodeIds: new Set(results.flatMap(r => [...r.nodeIds])),
  edges: new Set(results.flatMap(r => [...r.edges])),
});

/**
 * Collect 1-hop neighbors of `nodeId` that satisfy `predicate`,
 * along with their connecting edge keys.
 */
const collectDirectNeighbors = (
  nodeId: string,
  graphEdges: ConfigurationMapEdge[],
  predicate: (neighborId: string) => boolean
): BfsResult => {
  const nodeIds = new Set<string>();
  const edges = new Set<string>();

  graphEdges.forEach(edge => {
    if (edge.source === nodeId && predicate(edge.target)) {
      nodeIds.add(edge.target);
      edges.add(`${edge.source}-${edge.target}`);
    }
    if (edge.target === nodeId && predicate(edge.source)) {
      nodeIds.add(edge.source);
      edges.add(`${edge.source}-${edge.target}`);
    }
  });

  return { nodeIds, edges };
};

/**
 * Compute the set of connected nodes and edges for the selected node.
 *
 * **Field selected** → transitive field→field formula chains in both
 * directions, plus 1-hop to hooks/rules that directly reference the field.
 *
 * **Hook / rule selected** → three separate walks:
 *  1. *Direct field edges*: 1-hop to dependency and target fields.
 *  2. *Upstream formula chain*: from those fields, walk upstream following
 *     **field → field** edges only (formula chains).
 *  3. *Run-after chain* (hooks only): transitive BFS in **both** directions
 *     following **hook → hook** edges only (run_after ordering).
 *
 * This prevents shared fields like `document_id` from pulling in unrelated
 * hooks/rules, while still showing the full formula ancestry and the
 * complete run_after chain.
 */
export const computeConnected = (
  nodeId: string,
  graphEdges: ConfigurationMapEdge[],
  hookIds: Set<string>,
  ruleIds: Set<string>
): BfsResult => {
  const isFieldNode = (id: string) => !hookIds.has(id) && !ruleIds.has(id);
  const start = new Set([nodeId]);
  const startResult: BfsResult = { nodeIds: start, edges: new Set() };

  // --- Field selected ---
  if (isFieldNode(nodeId)) {
    const upFields = bfsWalk(start, graphEdges, upstream, isFieldNode);
    const downFields = bfsWalk(start, graphEdges, downstream, isFieldNode);
    const directNonFields = collectDirectNeighbors(
      nodeId,
      graphEdges,
      id => !isFieldNode(id)
    );
    return mergeResults(startResult, upFields, downFields, directNonFields);
  }

  // --- Hook / rule selected ---
  const directFields = collectDirectNeighbors(nodeId, graphEdges, isFieldNode);
  const upstreamChain = bfsWalk(
    directFields.nodeIds,
    graphEdges,
    upstream,
    isFieldNode
  );

  // Run-after chain: hook→hook edges, both directions (hooks only)
  if (hookIds.has(nodeId)) {
    const hookEdges = graphEdges.filter(
      e => hookIds.has(e.source) && hookIds.has(e.target)
    );
    const runAfterUp = bfsWalk(start, hookEdges, upstream);
    const runAfterDown = bfsWalk(start, hookEdges, downstream);
    return mergeResults(
      startResult,
      directFields,
      upstreamChain,
      runAfterUp,
      runAfterDown
    );
  }

  return mergeResults(startResult, directFields, upstreamChain);
};
